import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Search, ShieldAlert, Sparkles, Wrench } from 'lucide-react';
import { PRISM_VERSION, RELEASES, type Release } from '@prism/contracts';
import { useMe } from '../api/hooks/me';
import { matchesInBrief, releaseMatches, releaseSummary, upgradeGuideSection } from '../lib/release-notes';
import type { ReleaseNotesScope } from '../lib/release-notes-pdf';
import { Modal } from './Modal';

/** The version under the navigation; opens the release notes. */
export function VersionBadge() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="rail-version" onClick={() => setOpen(true)} title="What’s new in PRISM" data-testid="version">v{PRISM_VERSION}</button>
      {open && <ReleaseNotesDialog onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Make and download the release notes PDF; pdfmake loads only now. Like the window, the PDF has Before you upgrade
 * only for someone with the admin permission.
 */
export function useReleaseNotesPdf() {
  const me = useMe();
  const admin = me.data?.permissions.includes('admin') ?? false;
  const [busy, setBusy] = useState<ReleaseNotesScope | null>(null);
  const [error, setError] = useState<string | null>(null);
  const download = async (scope: ReleaseNotesScope) => {
    setBusy(scope);
    setError(null);
    try {
      const { downloadReleaseNotesPdf } = await import('../lib/release-notes-pdf');
      await downloadReleaseNotesPdf({ scope, version: PRISM_VERSION, releases: RELEASES, admin });
    } catch (e) {
      setError(`The PDF could not be made: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };
  return { busy, error, download };
}

export function PdfButtons({ pdf }: { pdf: ReturnType<typeof useReleaseNotesPdf> }) {
  return (
    <span role="group" aria-label="Download PDF" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <span className="faint" style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12 }}><Download size={13} /> Download PDF</span>
      <button className="btn sm" disabled={pdf.busy !== null} onClick={() => void pdf.download('release')} data-testid="release-notes-pdf-release">{pdf.busy === 'release' ? 'Making…' : 'This release'}</button>
      <button className="btn sm" disabled={pdf.busy !== null} onClick={() => void pdf.download('all')} data-testid="release-notes-pdf-all">{pdf.busy === 'all' ? 'Making…' : 'All releases'}</button>
    </span>
  );
}

/** The release notes: each release in brief, its details on request, a search, and the PDF. */
export function ReleaseNotesDialog({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const admin = me.data?.permissions.includes('admin') ?? false;
  const [query, setQuery] = useState('');
  // The newest release opens to its summary; the others show their heading until opened.
  // What a person opened or closed; a search opens what it finds, and closing one of those still works.
  const [brief, setBrief] = useState<Map<string, boolean>>(() => new Map([[RELEASES[0]!.version, true]]));
  const [details, setDetails] = useState<Map<string, boolean>>(() => new Map());
  const [searchOverrides, setSearchOverrides] = useState<{ brief: Map<string, boolean>; details: Map<string, boolean> }>({ brief: new Map(), details: new Map() });
  const pdf = useReleaseNotesPdf();
  const searching = query.trim().length > 0;
  const shown = useMemo(() => RELEASES.filter((r) => releaseMatches(r, query, admin)), [query, admin]);
  const onSearch = (q: string) => {
    setQuery(q);
    setSearchOverrides({ brief: new Map(), details: new Map() });
  };
  const isOpen = (r: Release) => {
    if (searching) {
      const b = searchOverrides.brief.get(r.version) ?? true;
      const d = searchOverrides.details.get(r.version) ?? (details.get(r.version) ?? !matchesInBrief(r, query, admin));
      return { brief: b, details: b && d };
    }
    const b = brief.get(r.version) ?? false;
    return { brief: b, details: b && (details.get(r.version) ?? false) };
  };
  const set = (which: 'brief' | 'details', v: string, open: boolean) => {
    if (searching) {
      setSearchOverrides((o) => ({ ...o, [which]: new Map(o[which]).set(v, open) }));
      if (which === 'details' && open) setSearchOverrides((o) => ({ ...o, brief: new Map(o.brief).set(v, true) }));
    }
    (which === 'brief' ? setBrief : setDetails)((m) => new Map(m).set(v, open));
    if (which === 'details' && open) setBrief((m) => new Map(m).set(v, true));
  };
  return (
    <Modal
      title={`PRISM ${PRISM_VERSION}`}
      wide
      onClose={onClose}
      actions={<>
        <PdfButtons pdf={pdf} />
        <span style={{ flex: 1 }} />
        <button className="btn" onClick={onClose}>Close</button>
      </>}
    >
      <div className="search-input" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <Search size={14} />
        <input className="input" style={{ flex: 1 }} placeholder="Search the release notes" aria-label="Search the release notes" value={query} onChange={(e) => onSearch(e.target.value)} data-testid="release-notes-search" />
      </div>
      {pdf.error && <p className="banner error" role="alert" data-testid="release-notes-pdf-error">{pdf.error}</p>}
      <div className="release-list" data-testid="version-history">
        {shown.length === 0 && <p className="muted" data-testid="release-notes-none">No release mentions “{query.trim()}”.</p>}
        {shown.map((r) => {
          // A search opens every release it finds, and its details when the words are only there.
          const open = isOpen(r);
          return (
            <ReleaseEntry
              key={r.version} release={r} admin={admin} brief={open.brief} details={open.details}
              onToggleBrief={() => set('brief', r.version, !open.brief)}
              onToggleDetails={() => set('details', r.version, !open.details)}
            />
          );
        })}
      </div>
    </Modal>
  );
}

function ReleaseEntry({ release: r, admin, brief, details, onToggleBrief, onToggleDetails }: {
  release: Release; admin: boolean; brief: boolean; details: boolean; onToggleBrief: () => void; onToggleDetails: () => void;
}) {
  const guide = upgradeGuideSection(r);
  const hasUpgradeNotes = (r.upgradeSummary?.length ?? 0) > 0 || (r.beforeUpgrade?.length ?? 0) > 0;
  return (
    <section className="release" data-testid={`release-${r.version}`} data-open={brief ? 'true' : 'false'}>
      <h3>
        <button type="button" aria-expanded={brief} onClick={onToggleBrief} data-testid="release-toggle"
          style={{ display: 'inline-flex', gap: 8, alignItems: 'baseline', background: 'none', border: 0, padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer', textAlign: 'left' }}>
          {brief ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          <span className="mono" style={{ whiteSpace: 'nowrap' }}>{r.version}</span> <span>{r.title}</span>
        </button>
        <span className="faint" style={{ fontWeight: 400, fontSize: 12, whiteSpace: 'nowrap' }}>{r.date}</span>
      </h3>
      {brief && (
        <>
          <ul data-testid="release-summary">{releaseSummary(r).map((s) => <li key={s}><Sparkles size={12} color="var(--accent-strong)" /> {s}</li>)}</ul>
          {admin && hasUpgradeNotes && (
            <div data-testid="before-upgrade" style={{ border: '1px solid var(--border-strong)', background: 'var(--warning-soft)', borderRadius: 8, padding: '8px 12px', margin: '8px 0 6px' }}>
              <strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}><ShieldAlert size={14} color="var(--warning)" /> Before you upgrade</strong>
              {r.upgradeSummary && r.upgradeSummary.length > 0 ? (
                <ul style={{ margin: '6px 0 0' }} data-testid="upgrade-summary">{r.upgradeSummary.map((b) => <li key={b}>{b}</li>)}</ul>
              ) : r.beforeUpgrade && r.beforeUpgrade.length > 0 && !details ? (
                <details style={{ marginTop: 4 }} data-testid="before-upgrade-collapsed">
                  <summary style={{ cursor: 'pointer', fontSize: 13 }}>{r.beforeUpgrade.length} {r.beforeUpgrade.length === 1 ? 'note' : 'notes'} for administrators</summary>
                  <ul style={{ margin: '6px 0 0' }}>{r.beforeUpgrade.map((b) => <li key={b}>{b}</li>)}</ul>
                </details>
              ) : null}
              {guide && (
                <p className="faint" style={{ margin: '6px 0 0', fontSize: 12 }} data-testid="upgrade-guide" title={`${guide.file}#${guide.anchor}`}>
                  The steps: {guide.file}, “{guide.section}”.
                </p>
              )}
            </div>
          )}
          <button type="button" className="btn ghost sm" aria-expanded={details} onClick={onToggleDetails} data-testid="release-details-toggle" style={{ marginTop: 4 }}>
            {details ? 'Hide details' : 'Show details'}
          </button>
          {details && (
            <div data-testid="release-details" style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {admin && r.beforeUpgrade && r.beforeUpgrade.length > 0 && (
                <div data-testid="before-upgrade-details">
                  <strong style={{ fontSize: 13 }}>Before you upgrade, in full</strong>
                  <ul style={{ marginTop: 4 }}>{r.beforeUpgrade.map((b) => <li key={b}><ShieldAlert size={12} color="var(--warning)" /> {b}</li>)}</ul>
                </div>
              )}
              {r.features.length > 0 && <ul data-testid="release-features">{r.features.map((f) => <li key={f}><Sparkles size={12} color="var(--accent-strong)" /> {f}</li>)}</ul>}
              {r.fixes.length > 0 && <ul data-testid="release-fixes">{r.fixes.map((f) => <li key={f}><Wrench size={12} color="var(--ready)" /> {f}</li>)}</ul>}
            </div>
          )}
        </>
      )}
    </section>
  );
}
